import { useEffect } from 'react'
import { useRxCollection } from 'rxdb-hooks'

import { canPlayCard, getPlayableCardsSnoozeSelector } from '~/boundedContext/cards/utils/card'
import { Card } from '~/db/types'
import { useAccountStore } from '~/stores/account'
import { useCardsGameStore } from '~/stores/cards-game'
import { useSettingsStore } from '~/stores/settings'

export const CardsGameBridge = () => {
  const userId = useAccountStore(state => state.id)
  const sourceLanguage = useSettingsStore(state => state.sourceLanguage.value)
  const learnLanguage = useSettingsStore(state => state.learnLanguage.value)
  const cardsFront = useSettingsStore(state => state.cardsFront)
  const setAllCards = useCardsGameStore(state => state.setAllCards)
  const resolveDocumentsFromIds = useCardsGameStore(state => state.resolveDocumentsFromIds)
  const onUpdate = useCardsGameStore(state => state.onUpdate)
  const onRemove = useCardsGameStore(state => state.onRemove)
  const reset = useCardsGameStore(state => state.reset)
  const setIsInitialized = useCardsGameStore(state => state.setIsInitialized)

  const cardsCollection = useRxCollection<Card>('cards')

  useEffect(() => {
    if (!learnLanguage || !sourceLanguage || !cardsCollection || !userId) return

    const collectionSubscription = cardsCollection
      .find({
        selector: {
          learnLanguage,
          sourceLanguage,
          userId,
          ...getPlayableCardsSnoozeSelector(cardsFront),
        },
      })
      .$.subscribe(collection => {
        const { allCards: prevAllCards, isInitialized, activeItemIndex, shuffledCardIds } = useCardsGameStore.getState()

        setAllCards(collection)

        // Initial zustand state hydration
        if (!isInitialized) {
          resolveDocumentsFromIds(collection)
          setIsInitialized(true)

          return
        }

        const isAdded = collection.length > prevAllCards.length
        // Only reset if a user didn't start the game yet
        const isFirstCard = shuffledCardIds.length === activeItemIndex + 1
        const isUnknownStack = shuffledCardIds.length !== prevAllCards.length
        if (isAdded && isFirstCard && !isUnknownStack) reset(collection)
      })
    const updateSubscription = cardsCollection.update$.subscribe(({ documentData }) => onUpdate(documentData))
    const removeSubscription = cardsCollection.remove$.subscribe(({ documentId }) => onRemove(documentId))

    return () => {
      collectionSubscription?.unsubscribe()
      updateSubscription?.unsubscribe()
      removeSubscription?.unsubscribe()
    }
  }, [
    cardsCollection,
    cardsFront,
    learnLanguage,
    sourceLanguage,
    userId,
    setIsInitialized,
    resolveDocumentsFromIds,
    setAllCards,
    reset,
    onUpdate,
    onRemove,
  ])

  useEffect(() => {
    const { shuffledCards, activeItemIndex, onRemove: removeFromStack } = useCardsGameStore.getState()

    shuffledCards
      .slice(0, activeItemIndex + 1)
      .filter(card => !canPlayCard(card, cardsFront))
      .forEach(card => removeFromStack(card.id))
  }, [cardsFront])

  return null
}
